import { useMemo, useState } from 'react';
import {
  ENTI, TIPI, FORMATS, SEZIONI, BLOCCHI_INIZIALI, PROPOSTE_INIZIALI,
  CONTROLLI, CORPUS, SCHEDE, SKILL, SEQUENZE,
} from './data/atti';
import type { Blocco, Controllo, Scheda } from './data/atti';
import type { ScelteAtto } from './AvvioAtto';

interface Props {
  scelte: ScelteAtto;
  schedaIniziale?: Scheda;
  onIndietro: () => void;
}

/** Nome breve del tenant: non può dipendere dalla presenza del trattino lungo. */
function nomeBreve(nome: string) {
  const i = nome.indexOf('—');
  return (i >= 0 ? nome.slice(i + 1) : nome).trim();
}

/** Ripulisce l'output del modello: nessuna sintassi Markdown davanti a un
 *  funzionario che sta redigendo un atto. */
export function ripulisci(testo: string) {
  return String(testo || '')
    .split('\n')
    .map((r) => r
      .replace(/^\s{0,3}#{1,6}\s*/, '')
      .replace(/^\s{0,3}[-*+]\s+/, '')
      .replace(/^\s{0,3}>\s?/, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function EditorAtto({ scelte, schedaIniziale = 'guida', onIndietro }: Props) {
  const [scheda, setScheda] = useState<Scheda>(schedaIniziale);
  const [focus, setFocus] = useState(false);
  const [sezione, setSezione] = useState('Base giuridica');
  const [blocchi, setBlocchi] = useState<Blocco[]>(BLOCCHI_INIZIALI);
  const [proposte, setProposte] = useState(PROPOSTE_INIZIALI);
  const [verificato, setVerificato] = useState(false);
  const [fonteId, setFonteId] = useState(CORPUS[0]!.id);
  const [spiegati, setSpiegati] = useState<string[]>([]);
  const [domanda, setDomanda] = useState('');
  const [risposta, setRisposta] = useState('');
  const [cercando, setCercando] = useState(false);
  const [skillFatte, setSkillFatte] = useState<string[]>([]);

  const ente = ENTI.find((e) => e.id === scelte.ente) ?? ENTI[0]!;
  const tipo = TIPI.find((t) => t.id === scelte.tipo) ?? TIPI[1]!;
  const formats = FORMATS[scelte.tipo] ?? FORMATS.decreto!;
  const format = formats.find((f) => f.id === scelte.format) ?? formats[0]!;
  const fonte = CORPUS.find((f) => f.id === fonteId) ?? CORPUS[0]!;
  const sequenza = SEQUENZE[scelte.tipo] ?? SEQUENZE.decreto!;
  const passiIa = blocchi.filter((b) => b.ia).length + 2;

  // L'avanzamento si deriva dal RUOLO del blocco, non dall'attacco in italiano:
  // la stessa base giuridica arriva da «Proponi» come VISTI e da «Consulta»
  // come VISTO. Legarla alla parola significa perdere il progresso.
  const haBase = blocchi.some((b) => b.ruolo === 'base-giuridica');
  const haDispositivo = blocchi.some((b) => b.ruolo === 'dispositivo');
  const haStorico = blocchi.some((b) => b.ruolo === 'storico');

  const passi = useMemo(() => {
    const lista: { titolo: string; nota: string; fatto: boolean; vai?: Scheda }[] = [
      { titolo: 'Compila i campi dell’atto', nota: 'Programma, dotazione, numero di proposte: entrano nei segnaposto del format.', fatto: true },
      { titolo: 'Richiama i visti obbligatori', nota: 'Regolamento comune e programma: il format li prevede in apertura.', fatto: true },
      { titolo: 'Motiva l’esito dell’istruttoria', nota: 'Collega il considerato al verbale e all’articolo dell’avviso applicato.', fatto: true },
      { titolo: 'Fonda la base giuridica', nota: 'Serve l’articolo sulla selezione delle operazioni e quello dei criteri dell’avviso.', fatto: haBase, vai: 'proponi' as Scheda },
      { titolo: 'Scrivi il dispositivo', nota: 'Approvazione dell’elenco e impegno di spesa, in articoli numerati.', fatto: haDispositivo, vai: 'proponi' as Scheda },
      { titolo: 'Produci gli allegati richiamati', nota: 'L’Allegato B è citato ma non esiste: va prodotto o il richiamo va rimosso.', fatto: false, vai: 'verifica' as Scheda },
      { titolo: 'Manda in revisione', nota: 'Nessun atto è protocollabile senza validazione umana registrata.', fatto: false },
    ];
    const i = lista.findIndex((p) => !p.fatto);
    return lista.map((p, k) => ({ ...p, corrente: k === i }));
  }, [haBase, haDispositivo]);

  const controlli: Controllo[] = haStorico
    ? [...CONTROLLI, { titolo: 'Richiamo a norma abrogata nel testo', nota: 'Presente un richiamo storico: verifica che non sia usato come base giuridica e che la norma vigente sia citata a parte.', esito: 'attenzione' }]
    : CONTROLLI;

  async function chiediAlCorpus() {
    const q = domanda.trim();
    if (!q || cercando) return;
    setCercando(true); setRisposta('');
    try {
      // TODO: POST /api/assistenza (gateway on-premise). NON chiamare un
      // provider esterno dal browser: aggirerebbe il vincolo sui dati sensibili.
      const r = await fetch('/api/assistenza', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: 'Esperto di normativa europea sulle politiche di coesione e aiuti di Stato. Rispondi in italiano, massimo 4 frasi, citando gli articoli. TESTO SEMPLICE: nessun Markdown. Se non hai la fonte, dichiaralo.',
          prompt: 'Contesto: redazione di un atto per un programma ' + tipo.nome + '.\nDomanda: ' + q,
        }),
      });
      const j = (await r.json()) as { testo?: string };
      setRisposta(ripulisci(j.testo ?? '') || 'Nel corpus attivo non è presente una fonte che risponda con certezza a questa domanda.');
    } catch {
      setRisposta('Assistente non raggiungibile: la domanda resta in coda e il corpus è consultabile per fonte.');
    } finally {
      setCercando(false);
    }
  }

  return (
    <div className="ui">
      <header className="ui-hdr">
        <a className="ui-back" href="/servizi">← Servizi</a>
        <div className="ui-titolo">
          <span className="ui-titolo-t">Decreto 412/2026 — Bando formazione 2/2026</span>
          <span className="ui-mono">{nomeBreve(ente.nome)} · format {format.versione.split(' ')[0]}</span>
        </div>
        <span className="ui-stato-bozza">Bozza · non protocollabile</span>
        <div className="ui-hdr-azioni">
          <span className="ui-presenza"><span className="ui-avatar">GR</span>g.rizzo sta scrivendo</span>
          {/* L'orchestrazione deve essere raggiungibile in un clic da qualunque
              punto dell'editor: è il modo in cui si lavora su atti codificati. */}
          <button type="button" className="ui-btn indigo" onClick={() => setScheda('skill')}>Orchestra le skill</button>
          <button type="button" className="ui-btn secondario" onClick={() => setFocus(!focus)}>
            {focus ? 'Esci dalla concentrazione' : 'Modalità concentrazione'}
          </button>
          <button type="button" className="ui-btn secondario" onClick={onIndietro}>Cambia format</button>
          <button type="button" className="ui-btn primario">Invia a revisione</button>
        </div>
      </header>

      <div className="ui-corpo">
        {!focus && (
          <aside className="ui-struttura">
            <div className="ui-etichetta">STRUTTURA DEL FORMAT</div>
            <div className="ui-sezioni">
              {SEZIONI.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={s === sezione ? 'sel' : s.startsWith('Allegato B') ? 'manca' : undefined}
                  onClick={() => setSezione(s)}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="ui-campi">
              <div className="ui-etichetta">CAMPI DELL'ATTO</div>
              <label>Programma<input type="text" defaultValue="FSE+" /></label>
              <label>Dotazione<input type="text" defaultValue="€ 4.200.000" /></label>
              <label>Proposte ammesse<input type="text" defaultValue="34" /></label>
              <p>I campi entrano nel testo dove compare il segnaposto: si cambiano qui, non nel documento.</p>
            </div>
          </aside>
        )}

        <main className="ui-foglio-area">
          <div className="ui-foglio">
            <div className="ui-intestazione">IL DIRIGENTE GENERALE</div>
            {blocchi.map((b) => (
              <div key={b.id} className={b.ruolo === 'storico' ? 'ui-blocco storico' : b.ia ? 'ui-blocco ia' : 'ui-blocco'}>
                <p><strong>{b.attacco}</strong> {b.testo}</p>
                {b.ia && (
                  <div className="ui-marcatura">
                    <span className="ui-mono ui-badge">
                      {b.ruolo === 'storico' ? 'richiamo storico · non base giuridica' : "proposto dall'IA · confermato da te"}
                    </span>
                    <span className="ui-fonti">{b.fonti}</span>
                  </div>
                )}
              </div>
            ))}
            {!haDispositivo && (
              <div className="ui-segnaposto">
                <div className="ui-segnaposto-t">DECRETA</div>
                <p>Segnaposto del format: il dispositivo è ancora vuoto. La guida ti dice cosa serve, l'assistente ha una proposta.</p>
              </div>
            )}
          </div>
          <p className="ui-salvato">Salvato alle 09:41 · {passiIa} passaggi assistiti · format {format.versione.split(' ')[0]}</p>
        </main>

        {/* L'assistente è LATERALE: le basi flex sono tarate perché la colonna
            che cede sia la struttura, non lui. */}
        <aside className="ui-assistente">
          <div className="ui-assistente-testa">
            <h2>Assistente</h2>
            <span className="ui-mono ui-tag">on-prem</span>
          </div>

          <div className="ui-schede">
            {SCHEDE.map((x) => (
              <button
                key={x.id}
                type="button"
                className={scheda === x.id ? 'sel' : undefined}
                aria-pressed={scheda === x.id}
                onClick={() => setScheda(x.id)}
              >
                {x.nome}
              </button>
            ))}
          </div>

          {scheda === 'guida' && (
            <div className="ui-pannello">
              <p className="ui-p">I passi previsti per un <strong>{tipo.nome}</strong> con questo format. Il passo corrente è quello che ti blocca.</p>
              {passi.map((p, i) => (
                <div key={p.titolo} className={p.corrente ? 'ui-passo-g corrente' : p.fatto ? 'ui-passo-g fatto' : 'ui-passo-g'}>
                  <span className="ui-passo-num">{p.fatto ? '✓' : i + 1}</span>
                  <div>
                    <div className="ui-passo-tit">{p.titolo}</div>
                    <div className="ui-passo-not">{p.nota}</div>
                    {p.corrente && (
                      <div className="ui-passo-azioni">
                        {p.vai && <button type="button" className="ui-btn chiaro" onClick={() => setScheda(p.vai!)}>Vai</button>}
                        <button type="button" className="ui-btn fantasma" onClick={() => setScheda('consulta')}>Cosa dice la norma</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {scheda === 'proponi' && (
            <div className="ui-pannello">
              {proposte.map((p) => (
                <div className="ui-proposta" key={p.id}>
                  <div className="ui-etichetta">{p.sezione}</div>
                  <p>{p.testo}</p>
                  <div className="ui-fonti-lista">
                    {p.fonti.map((f) => <div key={f.testo}><span /> {f.testo}</div>)}
                  </div>
                  <div className="ui-proposta-azioni">
                    <button
                      type="button"
                      className="ui-btn chiaro"
                      onClick={() => {
                        setBlocchi([...blocchi, {
                          id: 'x' + p.id, ruolo: p.ruolo, attacco: p.attacco, testo: p.inserito, ia: true,
                          fonti: p.fonti.map((f) => f.testo.split(' — ')[0]).join(' · '),
                        }]);
                        setProposte(proposte.filter((x) => x.id !== p.id));
                        setVerificato(false);
                      }}
                    >
                      Inserisci
                    </button>
                    <button type="button" className="ui-btn fantasma" onClick={() => setProposte(proposte.map((x) => x.id === p.id ? { ...x, testo: 'Formulazione più stretta: ' + p.testo.charAt(0).toLowerCase() + p.testo.slice(1) } : x))}>
                      Riformula
                    </button>
                    <button type="button" className="ui-btn piatto" onClick={() => setProposte(proposte.filter((x) => x.id !== p.id))}>Scarta</button>
                  </div>
                </div>
              ))}
              {proposte.length === 0 && (
                <div className="ui-vuoto">
                  <div>Nessuna proposta in sospeso</div>
                  <p>Continua a scrivere: l'assistente propone quando incontra una sezione da fondare su una fonte.</p>
                </div>
              )}
              <div className="ui-allarme">
                <div>Manca una base giuridica</div>
                <p>L'Allegato B (esclusi) è richiamato dal dispositivo ma non esiste, e nessuna fonte nel corpus giustifica l'omissione.</p>
              </div>
            </div>
          )}

          {scheda === 'verifica' && (
            <div className="ui-pannello">
              {!verificato ? (
                <div className="ui-vuoto">
                  <p>Controlla il testo contro la check-list dei controlli, i criteri dell'avviso e la vigenza delle norme citate. Non modifica nulla: elenca.</p>
                  <button type="button" className="ui-btn chiaro largo" onClick={() => setVerificato(true)}>Esegui la verifica</button>
                </div>
              ) : (
                <>
                  <div className="ui-esito">
                    <span>{controlli.filter((c) => c.esito === 'errore').length} problema bloccante, {controlli.filter((c) => c.esito === 'attenzione').length} da valutare</span>
                    <button type="button" className="ui-btn piatto" onClick={() => setVerificato(false)}>Ripeti</button>
                  </div>
                  {controlli.map((c) => (
                    <div className={'ui-controllo ' + c.esito} key={c.titolo}>
                      <span className="ui-punto" aria-hidden="true" />
                      <div><div className="ui-controllo-t">{c.titolo}</div><div className="ui-controllo-n">{c.nota}</div></div>
                    </div>
                  ))}
                  <p className="ui-piccolo">La verifica è assistita: non sostituisce il controllo di primo livello.</p>
                </>
              )}
            </div>
          )}

          {scheda === 'consulta' && (
            <div className="ui-pannello">
              <p className="ui-p">Regolamenti e corpus normativo, senza uscire dall'atto. Scegli la fonte e leggi l'articolo: da qui lo citi nel testo.</p>
              <div className="ui-pill-fonti">
                {CORPUS.map((f) => (
                  <button key={f.id} type="button" className={f.id === fonteId ? 'sel' : undefined} aria-pressed={f.id === fonteId} onClick={() => setFonteId(f.id)}>
                    {f.breve}
                  </button>
                ))}
              </div>

              <div className="ui-interroga">
                <div className="ui-etichetta">INTERROGA IL CORPUS</div>
                <div className="ui-interroga-riga">
                  <input type="text" value={domanda} onChange={(e) => setDomanda(e.target.value)} placeholder="es. quale intensità massima di aiuto posso concedere?" />
                  <button type="button" className="ui-btn chiaro" onClick={() => void chiediAlCorpus()} disabled={cercando}>
                    {cercando ? 'Cerco…' : 'Chiedi'}
                  </button>
                </div>
                <p className="ui-piccolo">Cerca nelle versioni attive dell'archivio. Se la fonte manca, l'assistente lo dichiara invece di rispondere.</p>
                {risposta && <p className="ui-risposta">{risposta}</p>}
              </div>

              <div className="ui-fonte-testa">
                <span>{fonte.titolo}</span>
                <span className={fonte.vigenza === 'vigente' ? 'ui-vig ok' : 'ui-vig no'}>{fonte.vigenza}</span>
                <div className="ui-piccolo">{fonte.meta}</div>
              </div>

              {fonte.articoli.map((a, i) => {
                const chiave = fonte.id + '-' + i;
                const vigente = fonte.vigenza === 'vigente';
                return (
                  <div className="ui-articolo" key={chiave}>
                    <div className="ui-mono ui-rif">{a.rif}</div>
                    <div className="ui-articolo-t">{a.rubrica}</div>
                    <p>{a.testo}</p>
                    <div className="ui-articolo-azioni">
                      {/* Regola non negoziabile: una norma abrogata NON entra
                          nella forma del visto. */}
                      <button
                        type="button"
                        className={vigente ? 'ui-btn viola' : 'ui-btn storico'}
                        onClick={() => {
                          setBlocchi([...blocchi, {
                            id: 'c' + Date.now(),
                            ruolo: vigente ? 'base-giuridica' : 'storico',
                            attacco: vigente ? fonte.attacco : 'RICHIAMATA, a soli fini ricostruttivi,',
                            testo: vigente
                              ? fonte.titolo.split('—')[0]!.trim() + ', ' + a.rif + ' — ' + a.rubrica.toLowerCase() + '…'
                              : fonte.titolo.split('—')[0]!.trim() + ', ' + a.rif + ' — atto non più vigente, non costituisce base giuridica…',
                            ia: true,
                            fonti: fonte.breve + ', ' + a.rif + (vigente ? '' : ' — abrogata'),
                          }]);
                          setVerificato(false);
                        }}
                      >
                        {vigente ? 'Cita nel testo' : 'Cita come riferimento storico'}
                      </button>
                      <button type="button" className="ui-btn piatto" onClick={() => setSpiegati(spiegati.includes(chiave) ? spiegati.filter((x) => x !== chiave) : [...spiegati, chiave])}>
                        Spiegami in breve
                      </button>
                    </div>
                    {!vigente && <p className="ui-avviso-storico">Non citabile come base giuridica: entra nel testo come richiamo storico, marcato, e la verifica lo segnala.</p>}
                    {spiegati.includes(chiave) && <p className="ui-spiegazione">{a.spiegazione}</p>}
                  </div>
                );
              })}
            </div>
          )}

          {scheda === 'skill' && (
            <div className="ui-pannello">
              <p className="ui-p">
                Le skill di UranIA, ordinate come servono per un <strong>{tipo.nome}</strong>. Puoi eseguirle
                una per una o lasciare che l'assistente le orchestri nell'ordine previsto.
              </p>
              <div className="ui-skill-azioni">
                <button type="button" className="ui-btn viola largo" onClick={() => setSkillFatte([...sequenza])} disabled={sequenza.every((id) => skillFatte.includes(id))}>
                  {sequenza.every((id) => skillFatte.includes(id)) ? 'Sequenza completata' : 'Esegui la sequenza per questo atto'}
                </button>
                <button type="button" className="ui-btn fantasma" onClick={() => setSkillFatte([])}>Azzera</button>
              </div>
              {sequenza.map((id, i) => {
                const k = SKILL[id]!;
                const fatta = skillFatte.includes(id);
                return (
                  <div className={fatta ? 'ui-skill fatta' : 'ui-skill'} key={id}>
                    <span className="ui-passo-num">{fatta ? '✓' : i + 1}</span>
                    <div>
                      <div className="ui-passo-tit">{k.nome}</div>
                      <div className="ui-passo-not">{k.cosa}</div>
                      {fatta
                        ? <p className="ui-skill-esito">{k.esito}</p>
                        : <button type="button" className="ui-btn chiaro" onClick={() => setSkillFatte([...skillFatte, id])}>Esegui</button>}
                    </div>
                  </div>
                );
              })}
              <p className="ui-piccolo">L'orchestrazione esegue le skill e riporta gli esiti: non modifica l'atto. Le proposte di testo restano da confermare una per una.</p>
            </div>
          )}

          <div className="ui-tracciabilita">
            <div className="ui-etichetta">TRACCIABILITÀ</div>
            <div className="ui-riga"><span>Passaggi assistiti</span><span className="ui-mono">{passiIa}</span></div>
            <div className="ui-riga"><span>Format</span><span className="ui-mono">{format.versione.split(' ')[0]}</span></div>
            <div className="ui-riga"><span>Versione archivio</span><span className="ui-mono">FSE+ v8</span></div>
            <div className="ui-riga"><span>Revisore</span><span className="ui-ambra">da assegnare</span></div>
            <button type="button" className="ui-btn fantasma largo">Esporta il fascicolo</button>
            <p className="ui-piccolo">
              Sistema di IA ai sensi del Reg. UE 2024/1689 (art. 50) e della L. 132/2025: le proposte vanno
              verificate prima di assumere decisioni amministrative.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
